(() => {
  'use strict';
  const state = window.__tomautoHeaderState || {
    globalsBound: false,
    mobileScrollY: 0,
    scrollFrame: 0
  };
  window.__tomautoHeaderState = state;
  state.cartRefreshTimer ??= 0;
  state.cartReturnFocus ??= null;
  state.cartRequestSyncInstalled ??= false;
  state.cartFormInterceptorInstalled ??= false;
  state.internalCartAdd ??= false;
  state.cartPromoOpen ??= false;
  state.cartPromoCode ??= '';
  state.cartPromoMessage ??= '';
  state.cartPromoStatus ??= '';
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
  function bindOnce(element, eventName, handler, key = eventName, options) {
    if (!element) return;
    const attribute = `data-tm-bound-${key}`;
    if (element.hasAttribute(attribute)) return;
    element.setAttribute(attribute, 'true');
    element.addEventListener(eventName, handler, options);
  }
  function cleanSearchQuery(query) {
    const cleaned = String(query || '')
      .replace(/&quot;|&#34;/gi, '"')
      .replace(/\b(?:tags?|marcas?|modelo|ano)\s\*:\s\*/gi, '')
      .replace(/["']/g, '')
      .replace(/\s+(?:AND|and)\s+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!cleaned) return '';
    return cleaned.charAt(0).toLocaleUpperCase('pt-PT') + cleaned.slice(1);
  }
  function runSearch(input) {
    if (!input?.form) return;
    const visibleQuery = input.value.trim();
    const query =
      visibleQuery === input.dataset.displaySearchQuery && input.dataset.rawSearchQuery
        ? input.dataset.rawSearchQuery
        : visibleQuery;
    if (!query) {
      input.focus();
      return;
    }
    const url = new URL(input.form.action, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    window.location.assign(`${url.pathname}${url.search}`);
  }
  function populateSearchFields() {
    const rawQuery = new URLSearchParams(window.location.search).get('q');
    if (!rawQuery) return;
    const displayQuery = cleanSearchQuery(rawQuery);
    ['search-bar-input', 'mobile-search-input'].forEach((id) => {
      const input = document.getElementById(id);
      if (!input) return;
      input.value = displayQuery;
      input.dataset.rawSearchQuery = rawQuery;
      input.dataset.displaySearchQuery = displayQuery;
    });
  }
  function formatMoney(amount) {
    const currency = window.Shopify?.currency?.active || 'EUR';
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency
    }).format((Number(amount) || 0) / 100);
  }
  function createElement(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }
  function safeInternalPath(value, fallback = '#') {
    if (!value) return fallback;
    try {
      const url = new URL(String(value), window.location.origin);
      if (url.origin !== window.location.origin || !/^https?:$/.test(url.protocol)) {
        return fallback;
      }
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return fallback;
    }
  }
  function safeImageUrl(value, fallback) {
    try {
      const url = new URL(String(value || fallback), window.location.href);
      return /^https?:$/.test(url.protocol) ? url.href : fallback;
    } catch {
      return fallback;
    }
  }
  function prepareCartPortal() {
    const overlay = document.getElementById('tm-cart-drawer-overlay');
    const drawer = document.getElementById('cart-dropdown');
    if (overlay && overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }
    if (drawer && drawer.parentElement !== document.body) {
      document.body.appendChild(drawer);
    }
  }
  async function fetchCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) {
      throw new Error(`Cart request failed: ${response.status}`);
    }
    return response.json();
  }
  function renderCartDrawer(cart) {
    const fragment = document.createDocumentFragment();
    if (!cart?.item_count) {
      const empty = createElement('div', 'tm-cart-drawer-empty');
      empty.append(
        createElement('strong', 'tm-cart-drawer-empty__title', 'O seu carrinho está vazio'),
        createElement('span', 'tm-cart-drawer-empty__text', 'Adicione uma peça para continuar.')
      );
      fragment.appendChild(empty);
      return fragment;
    }
    const fallbackImage =
      '//cdn.shopify.com/s/assets/no-image-2048-5e88c1b20e087fb7bbe9a3771824e743c244f437e4f8ba93bbf7b11b53f7824c_large.gif';
    const items = createElement('div', 'tomauto-mini-cart-items');
    (cart.items || []).forEach((item) => {
      const title = String(item.title || 'Produto');
      const quantity = Number(item.quantity) || 0;
      const productUrl = safeInternalPath(item.url);
      const row = createElement('div', 'tomauto-mini-cart-item');
      const imageLink = createElement('a', 'tomauto-mini-cart-item__image');
      imageLink.href = productUrl;
      imageLink.setAttribute('aria-label', title);
      const image = document.createElement('img');
      image.width = 64;
      image.height = 64;
      image.src = safeImageUrl(item.image, fallbackImage);
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      imageLink.appendChild(image);
      const info = createElement('div', 'tomauto-mini-cart-item__info');
      const titleLink = createElement('a', 'tomauto-mini-cart-item__title', title);
      titleLink.href = productUrl;
      titleLink.title = title;
      info.append(titleLink);
      const side = createElement('div', 'tomauto-mini-cart-item__side');
      const price = createElement(
        'strong',
        'tomauto-mini-cart-item__price',
        formatMoney(item.final_line_price)
      );
      const drawer = document.getElementById('cart-dropdown');
      const controls = createElement('div', 'tomauto-mini-cart-qty');
      controls.setAttribute('role', 'group');
      controls.setAttribute('aria-label', `Quantidade de ${title}`);
      const minus = createElement('button', 'tomauto-mini-cart-qty__button');
      minus.type = 'button';
      minus.dataset.cartKey = String(item.key || item.id || '');
      minus.dataset.quantity = String(Math.max(0, quantity - 1));
      minus.setAttribute('aria-label', quantity <= 1 ? `Remover ${title}` : `Diminuir quantidade de ${title}`);
      const minusIcon = document.createElement('img');
      minusIcon.src = drawer?.dataset.iconMinus || '';
      minusIcon.alt = '';
      minusIcon.width = 12;
      minusIcon.height = 12;
      minusIcon.setAttribute('aria-hidden', 'true');
      minus.appendChild(minusIcon);
      const quantityValue = createElement('span', 'tomauto-mini-cart-qty__value', String(quantity));
      quantityValue.setAttribute('aria-live', 'polite');
      const plus = createElement('button', 'tomauto-mini-cart-qty__button');
      plus.type = 'button';
      plus.dataset.cartKey = String(item.key || item.id || '');
      plus.dataset.quantity = String(quantity + 1);
      plus.setAttribute('aria-label', `Aumentar quantidade de ${title}`);
      const plusIcon = document.createElement('img');
      plusIcon.src = drawer?.dataset.iconPlus || '';
      plusIcon.alt = '';
      plusIcon.width = 12;
      plusIcon.height = 12;
      plusIcon.setAttribute('aria-hidden', 'true');
      plus.appendChild(plusIcon);
      controls.append(minus, quantityValue, plus);
      side.append(price, controls);
      row.append(imageLink, info, side);
      items.appendChild(row);
    });
    const promo = createElement('div', 'tomauto-mini-cart-promo');
    if (state.cartPromoOpen) promo.classList.add('is-open');
    const promoToggle = createElement('button', 'tomauto-mini-cart-promo__toggle');
    promoToggle.type = 'button';
    promoToggle.setAttribute('aria-expanded', state.cartPromoOpen ? 'true' : 'false');
    promoToggle.setAttribute('aria-controls', 'tm-cart-promo-panel');
    promoToggle.appendChild(createElement('span', '', 'Código promocional'));
    const promoChevron = document.createElement('img');
    promoChevron.className = 'tomauto-mini-cart-promo__chevron';
    promoChevron.src = document.getElementById('cart-dropdown')?.dataset.iconChevron || '';
    promoChevron.alt = '';
    promoChevron.width = 14;
    promoChevron.height = 14;
    promoChevron.setAttribute('aria-hidden', 'true');
    promoToggle.appendChild(promoChevron);
    const promoPanel = createElement('div', 'tomauto-mini-cart-promo__panel');
    promoPanel.id = 'tm-cart-promo-panel';
    promoPanel.hidden = !state.cartPromoOpen;
    const promoForm = createElement('form', 'tomauto-mini-cart-promo__form');
    promoForm.setAttribute('novalidate', '');
    const promoInput = document.createElement('input');
    promoInput.className = 'tomauto-mini-cart-promo__input';
    promoInput.type = 'text';
    promoInput.name = 'discount';
    promoInput.autocomplete = 'off';
    promoInput.placeholder = 'Introduzir código';
    promoInput.value = state.cartPromoCode || '';
    promoInput.setAttribute('aria-label', 'Código promocional');
    const promoApply = createElement('button', 'tomauto-mini-cart-promo__apply', 'Aplicar');
    promoApply.type = 'submit';
    promoForm.append(promoInput, promoApply);
    promoPanel.appendChild(promoForm);
    if (state.cartPromoMessage) {
      const promoMessage = createElement(
        'span',
        `tomauto-mini-cart-promo__message${state.cartPromoStatus ? ` is-${state.cartPromoStatus}` : ''}`,
        state.cartPromoMessage
      );
      promoMessage.setAttribute('role', 'status');
      promoPanel.appendChild(promoMessage);
    }
    promo.append(promoToggle, promoPanel);
    const summary = createElement('div', 'tomauto-mini-cart-summary');
    const totalRow = createElement('div', 'tomauto-mini-cart-total__row');
    totalRow.append(
      createElement('span', 'tomauto-mini-cart-total__label', 'Subtotal'),
      createElement('strong', 'tomauto-mini-cart-total__value', formatMoney(cart.total_price))
    );
    const shippingRow = createElement('div', 'tomauto-mini-cart-shipping');
    shippingRow.append(
      createElement('span', '', 'Envio PT continental'),
      createElement('strong', '', 'Grátis')
    );
    summary.append(totalRow, shippingRow);
    const buttons = createElement('div', 'tomauto-mini-cart-buttons');
    const checkout = createElement('a', 'checkout-button', 'FINALIZAR COMPRA');
    checkout.href = '/checkout';
    const paymentTemplate = document.getElementById('tm-cart-payment-methods-template');
    const paymentMethods = paymentTemplate?.content?.firstElementChild
      ? paymentTemplate.content.firstElementChild.cloneNode(true)
      : null;
    buttons.append(checkout);
    if (paymentMethods) buttons.append(paymentMethods);
    fragment.append(items, promo, summary, buttons);
    return fragment;
  }
  async function changeCartQuantity(itemKey, quantity) {
    if (!itemKey) return null;
    const root = window.Shopify?.routes?.root || '/';
    const drawer = document.getElementById('cart-dropdown');
    drawer?.setAttribute('aria-busy', 'true');
    state.internalCartAdd = true;
    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: String(itemKey),
          quantity: Math.max(0, Number(quantity) || 0)
        })
      });
      if (!response.ok) {
        throw new Error(`Cart change failed: ${response.status}`);
      }
      const cart = await response.json();
      updateCartHeader(cart);
      const content = $('#cart-dropdown .cart-dropdown-content');
      if (content) content.replaceChildren(renderCartDrawer(cart));
      return cart;
    } catch (error) {
      console.error('[TOMAUTO] Não foi possível alterar a quantidade.', error);
      return null;
    } finally {
      state.internalCartAdd = false;
      drawer?.removeAttribute('aria-busy');
    }
  }
  function normalizeDiscountText(value) {
    return String(value || '')
      .trim()
      .toLocaleLowerCase('pt-PT');
  }
  function collectCartDiscountLabels(cart) {
    const labels = [];
    const addLabel = (value) => {
      const normalized = normalizeDiscountText(value);
      if (normalized) labels.push(normalized);
    };
    const addApplication = (application) => {
      if (!application || typeof application !== 'object') return;
      addLabel(application.code);
      addLabel(application.title);
      addLabel(application.name);
    };
    (cart?.cart_level_discount_applications || []).forEach(addApplication);
    (cart?.discount_applications || []).forEach(addApplication);
    (cart?.items || []).forEach((item) => {
      (item?.discounts || []).forEach(addApplication);
      (item?.line_level_discount_allocations || []).forEach((allocation) => {
        addApplication(allocation);
        addApplication(allocation?.discount_application);
      });
      (item?.discount_allocations || []).forEach((allocation) => {
        addApplication(allocation);
        addApplication(allocation?.discount_application);
        addApplication(allocation?.source_discount_application);
      });
    });
    return labels;
  }
  function cartDiscountAmount(cart) {
    const cartTotalDiscount = Number(cart?.total_discount) || 0;
    const itemDiscounts = (cart?.items || []).reduce(
      (sum, item) => sum + (Number(item?.total_discount) || Number(item?.line_level_total_discount) || 0),
      0
    );
    const cartLevelDiscounts = (cart?.cart_level_discount_applications || []).reduce(
      (sum, application) => sum + (Number(application?.total_allocated_amount) || 0),
      0
    );
    return Math.max(cartTotalDiscount, itemDiscounts + cartLevelDiscounts);
  }
  function isSubmittedDiscountApplied(beforeCart, afterCart, code) {
    const normalizedCode = normalizeDiscountText(code);
    const labels = collectCartDiscountLabels(afterCart);
    if (normalizedCode && labels.some((label) => label === normalizedCode)) {
      return true;
    }
    const beforeTotal = Number(beforeCart?.total_price) || 0;
    const afterTotal = Number(afterCart?.total_price) || 0;
    const beforeDiscount = cartDiscountAmount(beforeCart);
    const afterDiscount = cartDiscountAmount(afterCart);
    return afterTotal < beforeTotal || afterDiscount > beforeDiscount;
  }
  async function applyCartDiscount(code) {
    const cleanCode = String(code || '').trim();
    if (!cleanCode) {
      state.cartPromoOpen = true;
      state.cartPromoMessage = 'Introduza um código de desconto.';
      state.cartPromoStatus = 'error';
      return null;
    }
    const root = window.Shopify?.routes?.root || '/';
    const drawer = document.getElementById('cart-dropdown');
    drawer?.setAttribute('aria-busy', 'true');
    state.internalCartAdd = true;
    try {
      const beforeCart = await fetchCart();
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ discount: cleanCode })
      });
      if (!response.ok) {
        throw new Error(`Cart discount failed: ${response.status}`);
      }
      const cart = await response.json();
      const applied = isSubmittedDiscountApplied(beforeCart, cart, cleanCode);
      state.cartPromoOpen = true;
      state.cartPromoCode = cleanCode;
      state.cartPromoMessage = applied
        ? 'Código aplicado. O desconto já foi atualizado no carrinho.'
        : 'Código inválido ou não aplicável a este carrinho.';
      state.cartPromoStatus = applied ? 'success' : 'error';
      updateCartHeader(cart);
      const content = $('#cart-dropdown .cart-dropdown-content');
      if (content) content.replaceChildren(renderCartDrawer(cart));
      return applied ? cart : null;
    } catch (error) {
      console.error('[TOMAUTO] Não foi possível aplicar o código de desconto.', error);
      state.cartPromoOpen = true;
      state.cartPromoCode = cleanCode;
      state.cartPromoMessage = 'Não foi possível validar o código. Tente novamente.';
      state.cartPromoStatus = 'error';
      const cart = await fetchCart().catch(() => null);
      const content = $('#cart-dropdown .cart-dropdown-content');
      if (cart && content) content.replaceChildren(renderCartDrawer(cart));
      return null;
    } finally {
      state.internalCartAdd = false;
      drawer?.removeAttribute('aria-busy');
    }
  }
  async function refreshCart({ renderDrawer = false } = {}) {
    const content = $('#cart-dropdown .cart-dropdown-content');
    try {
      const cart = await fetchCart();
      updateCartHeader(cart);
      if (renderDrawer && content) {
        content.replaceChildren(renderCartDrawer(cart));
      }
      return cart;
    } catch (error) {
      console.error('[TOMAUTO] Não foi possível atualizar o carrinho.', error);
      if (renderDrawer && content) {
        const message = createElement('div', 'tm-cart-drawer-empty');
        message.append(
          createElement('strong', 'tm-cart-drawer-empty__title', 'Não foi possível carregar o carrinho'),
          createElement('span', 'tm-cart-drawer-empty__text', 'Tente novamente.')
        );
        content.replaceChildren(message);
      }
      return null;
    }
  }
  function hideCart({ restoreFocus = true } = {}) {
    const drawer = document.getElementById('cart-dropdown');
    const overlay = document.getElementById('tm-cart-drawer-overlay');
    if (!drawer) return;
    drawer.classList.remove('active');
    drawer.setAttribute('aria-hidden', 'true');
    overlay?.classList.remove('active');
    overlay?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('tm-cart-drawer-open');
    document.getElementById('cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
    if (restoreFocus && state.cartReturnFocus instanceof HTMLElement) {
      state.cartReturnFocus.focus({ preventScroll: true });
    }
    state.cartReturnFocus = null;
  }
  async function showCart({ refresh = true, focusDrawer = true } = {}) {
    const drawer = document.getElementById('cart-dropdown');
    const overlay = document.getElementById('tm-cart-drawer-overlay');
    if (!drawer) return;
    closeDesktopMenu();
    closeMobileMenu();
    state.cartReturnFocus = document.activeElement;
    drawer.classList.add('active');
    drawer.setAttribute('aria-hidden', 'false');
    overlay?.classList.add('active');
    overlay?.setAttribute('aria-hidden', 'false');
    document.body.classList.add('tm-cart-drawer-open');
    document.getElementById('cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
    if (refresh) {
      await refreshCart({ renderDrawer: true });
    }
    if (focusDrawer) {
      window.requestAnimationFrame(() => {
        document.getElementById('tm-cart-drawer-close')?.focus({ preventScroll: true });
      });
    }
  }
  function getCartMutationType(value) {
    try {
      const raw = typeof value === 'string' ? value : value?.url || '';
      const path = new URL(raw, window.location.origin).pathname;
      if (/\/cart\/add(?:\.js)?\/?$/.test(path)) return 'add';
      if (/\/cart\/(?:change|update|clear)(?:\.js)?\/?$/.test(path)) return 'update';
      return null;
    } catch {
      return null;
    }
  }
  function syncCartMutation(type) {
    window.clearTimeout(state.cartRefreshTimer);
    state.cartRefreshTimer = window.setTimeout(async () => {
      const drawerOpen = document.getElementById('cart-dropdown')?.classList.contains('active');
      const shouldOpen =
        type === 'add' &&
        window.location.pathname.replace(/\/+$/, '') !== '/cart';
      const cart = await refreshCart({
        renderDrawer: drawerOpen || shouldOpen
      });
      if (shouldOpen && cart?.item_count) {
        await showCart({ refresh: false, focusDrawer: true });
      }
    }, 0);
  }
  function installCartRequestSync() {
    if (state.cartRequestSyncInstalled) return;
    state.cartRequestSyncInstalled = true;
    const nativeFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const mutationType = getCartMutationType(args[0]);
      const response = await nativeFetch(...args);
      if (mutationType && response.ok && !state.internalCartAdd) {
        syncCartMutation(mutationType);
      }
      return response;
    };
    if (
      window.XMLHttpRequest &&
      !window.XMLHttpRequest.prototype.__tomautoCartSync
    ) {
      const proto = window.XMLHttpRequest.prototype;
      const nativeOpen = proto.open;
      const nativeSend = proto.send;
      proto.open = function(method, url, ...rest) {
        this.__tomautoCartMutationType = getCartMutationType(url);
        return nativeOpen.call(this, method, url, ...rest);
      };
      proto.send = function(...args) {
        if (this.__tomautoCartMutationType) {
          this.addEventListener(
            'loadend',
            () => {
              if (this.status >= 200 && this.status < 400) {
                syncCartMutation(this.__tomautoCartMutationType);
              }
            },
            { once: true }
          );
        }
        return nativeSend.apply(this, args);
      };
      proto.__tomautoCartSync = true;
    }
  }
  function installCartFormInterceptor() {
    if (state.cartFormInterceptorInstalled) return;
    state.cartFormInterceptorInstalled = true;
    document.addEventListener(
      'submit',
      async (event) => {
        const form = event.target?.closest?.('form[action\*="/cart/add"]');
        if (!form || form.dataset.tomautoSubmitting === 'true') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        form.dataset.tomautoSubmitting = 'true';
        const submitControls = Array.from(
          form.querySelectorAll('button[type="submit"], input[type="submit"]')
        );
        submitControls.forEach((control) => {
          control.dataset.tomautoWasDisabled = control.disabled ? 'true' : 'false';
          control.disabled = true;
        });
        try {
          state.internalCartAdd = true;
          const response = await fetch(form.action, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          if (!response.ok) {
            let message = 'Não foi possível adicionar o produto ao carrinho.';
            try {
              const errorData = await response.json();
              message = errorData?.description || errorData?.message || message;
            } catch {}
            throw new Error(message);
          }
          const cart = await refreshCart({ renderDrawer: true });
          if (cart?.item_count) {
            document.dispatchEvent(
              new CustomEvent('cart:updated', {
                detail: { cart }
              })
            );
            await showCart({ refresh: false, focusDrawer: true });
          }
        } catch (error) {
          window.alert(
            error?.message || 'Não foi possível adicionar o produto ao carrinho.'
          );
        } finally {
          state.internalCartAdd = false;
          delete form.dataset.tomautoSubmitting;
          submitControls.forEach((control) => {
            control.disabled = control.dataset.tomautoWasDisabled === 'true';
            delete control.dataset.tomautoWasDisabled;
          });
        }
      },
      true
    );
  }
  function trapCartFocus(event) {
    const drawer = document.getElementById('cart-dropdown');
    if (event.key !== 'Tab' || !drawer?.classList.contains('active')) {
      return;
    }
    const focusable = Array.from(
      drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
  function updateCartHeader(cart) {
    if (!cart) return;
    const total = Number(cart.total_price) || 0;
    const count = Number(cart.item_count) || 0;
    const totalElement = document.getElementById('cart-total-amount');
    const bubble = $('.cart-count-bubble');
    if (totalElement) {
      totalElement.textContent = formatMoney(total);
    }
    if (!bubble) return;
    const visibleCount = $('span[aria-hidden="true"]', bubble);
    const accessibleCount = $('.tm-visually-hidden', bubble);
    if (visibleCount) {
      visibleCount.textContent = count < 100 ? String(count) : '99+';
    }
    if (accessibleCount) {
      accessibleCount.textContent = `${count} produto${count === 1 ? '' : 's'} no carrinho`;
    }
  }
  function getSubcategories(index) {
    const submenu = document.getElementById(`mobile-submenu-${index}`);
    if (!submenu) return [];
    return $$('.mobile-subcategory-link', submenu).map((link) => ({
      title: link.textContent.trim(),
      url: link.getAttribute('href')
    }));
  }
  function hideDesktopSubcategories() {
    $$('.desktop-menu-link.has-submenu[aria-expanded="true"]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
    const column = document.getElementById('desktop-subcategories-column');
    if (column) {
      column.classList.remove('has-content');
      column.replaceChildren();
    }
  }
  function showDesktopSubcategories(button) {
    const target = button?.dataset.target;
    const column = document.getElementById('desktop-subcategories-column');
    if (!target || !column) return;
    hideDesktopSubcategories();
    const index = Number.parseInt(target.replace('desktop-submenu-', ''), 10);
    const subcategories = getSubcategories(index);
    const links = subcategories.length
      ? subcategories
      : [
          { title: 'Todos os produtos', url: '/collections/all' },
          { title: 'Ofertas', url: '/' }
        ];
    const content = document.createElement('nav');
    content.className = 'desktop-subcategories-content active';
    content.setAttribute('aria-label', 'Subcategorias');
    const list = document.createElement('ul');
    list.className = 'desktop-subcategories-links';
    links.forEach((item) => {
      const wrapper = document.createElement('li');
      wrapper.className = 'desktop-subcategory-item';
      const link = document.createElement('a');
      link.className = 'desktop-subcategory-link';
      link.href = item.url;
      link.textContent = item.title;
      wrapper.appendChild(link);
      list.appendChild(wrapper);
    });
    content.appendChild(list);
    column.replaceChildren(content);
    column.classList.add('has-content');
    button.setAttribute('aria-expanded', 'true');
  }
  function openDesktopMenu() {
    const drawer = document.getElementById('custom-menu-drawer');
    if (!drawer) return;
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('tomauto-desktop-menu-open');
    document.body.classList.add('tomauto-desktop-menu-open', 'has-menu-open');
    document
      .getElementById('tm-header-menu-button')
      ?.setAttribute('aria-expanded', 'true');
    hideDesktopSubcategories();
  }
  function closeDesktopMenu() {
    const drawer = document.getElementById('custom-menu-drawer');
    if (!drawer) return;
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    document
      .getElementById('tm-header-menu-button')
      ?.setAttribute('aria-expanded', 'false');
    hideDesktopSubcategories();
    document.documentElement.classList.remove('tomauto-desktop-menu-open');
    document.body.classList.remove('tomauto-desktop-menu-open');
    if (!document.getElementById('mobile-menu-drawer')?.classList.contains('open')) {
      document.body.classList.remove('has-menu-open');
    }
  }
  function lockMobilePage() {
    if (document.body.classList.contains('tomauto-mobile-menu-open')) return;
    state.mobileScrollY =
      window.scrollY || document.documentElement.scrollTop || 0;
    document.documentElement.classList.add('tomauto-mobile-menu-open');
    document.body.classList.add('tomauto-mobile-menu-open');
    document.body.style.top = `-${state.mobileScrollY}px`;
  }
  function unlockMobilePage() {
    if (!document.body.classList.contains('tomauto-mobile-menu-open')) return;
    document.documentElement.classList.remove('tomauto-mobile-menu-open');
    document.body.classList.remove('tomauto-mobile-menu-open');
    document.body.style.removeProperty('top');
    window.scrollTo(0, state.mobileScrollY);
  }
  function hideMobileSubcategories() {
    $$('.mobile-subcategories').forEach((submenu) => {
      submenu.classList.remove('active');
      submenu.setAttribute('aria-hidden', 'true');
    });
    $$('.mobile-menu-link.has-submenu').forEach((link) => {
      link.classList.remove('active');
      link.setAttribute('aria-expanded', 'false');
    });
    const categories = document.getElementById('mobile-main-categories');
    const account = $('.mobile-account-section');
    if (categories) categories.style.display = 'flex';
    if (account) account.style.display = 'block';
  }
  function showMobileSubcategories(button) {
    const target = button?.dataset.target;
    const submenu = target ? document.getElementById(target) : null;
    if (!submenu) return;
    hideMobileSubcategories();
    const categories = document.getElementById('mobile-main-categories');
    const account = $('.mobile-account-section');
    if (categories) categories.style.display = 'none';
    if (account) account.style.display = 'none';
    submenu.classList.add('active');
    submenu.setAttribute('aria-hidden', 'false');
    button.classList.add('active');
    button.setAttribute('aria-expanded', 'true');
  }
  function openMobileMenu() {
    const drawer = document.getElementById('mobile-menu-drawer');
    const overlay = document.getElementById('mobile-menu-overlay');
    const button = document.getElementById('mobile-menu-button');
    if (!drawer || !overlay || !button) return;
    lockMobilePage();
    hideMobileSubcategories();
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.classList.add('active');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('has-menu-open');
    button.setAttribute('aria-expanded', 'true');
    button.setAttribute('aria-label', 'Fechar menu');
  }
  function closeMobileMenu() {
    const drawer = document.getElementById('mobile-menu-drawer');
    const overlay = document.getElementById('mobile-menu-overlay');
    const button = document.getElementById('mobile-menu-button');
    drawer?.classList.remove('open');
    drawer?.setAttribute('aria-hidden', 'true');
    overlay?.classList.remove('active');
    overlay?.setAttribute('aria-hidden', 'true');
    if (button) {
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-label', 'Abrir menu principal');
    }
    hideMobileSubcategories();
    unlockMobilePage();
    if (!document.getElementById('custom-menu-drawer')?.classList.contains('open')) {
      document.body.classList.remove('has-menu-open');
    }
  }
  function toggleMobileMenu() {
    const drawer = document.getElementById('mobile-menu-drawer');
    if (drawer?.classList.contains('open')) {
      closeMobileMenu();
    } else {
      openMobileMenu();
    }
  }
  function syncMobileHeaderScroll() {
    const header = $('.tm-header-shell');
    if (!header) return;
    if (window.innerWidth >= 1200) {
      header.classList.remove('tm-header-scrolled');
      return;
    }
    header.classList.toggle('tm-header-scrolled', window.scrollY > 36);
  }
  function scheduleMobileHeaderScroll() {
    if (state.scrollFrame) return;
    state.scrollFrame = window.requestAnimationFrame(() => {
      state.scrollFrame = 0;
      syncMobileHeaderScroll();
    });
  }
  function bindHeaderElements() {
    const desktopMenuButton = document.getElementById('tm-header-menu-button');
    const desktopDrawer = document.getElementById('custom-menu-drawer');
    const desktopClose = document.getElementById('drawer-close-button');
    const mobileMenuButton = document.getElementById('mobile-menu-button');
    const mobileDrawer = document.getElementById('mobile-menu-drawer');
    const mobileOverlay = document.getElementById('mobile-menu-overlay');
    const cartIcon = document.getElementById('cart-icon-bubble');
    const cartDrawer = document.getElementById('cart-dropdown');
    const cartClose = document.getElementById('tm-cart-drawer-close');
    const cartOverlay = document.getElementById('tm-cart-drawer-overlay');
    bindOnce(desktopMenuButton, 'click', openDesktopMenu, 'open-menu');
    bindOnce(desktopClose, 'click', closeDesktopMenu, 'close-menu');
    bindOnce(
      desktopDrawer,
      'click',
      (event) => {
        if (event.target === desktopDrawer) {
          closeDesktopMenu();
        }
      },
      'overlay-close'
    );
    $$('.desktop-menu-link.has-submenu').forEach((button) => {
      bindOnce(
        button,
        'click',
        () => showDesktopSubcategories(button),
        'show-submenu'
      );
    });
    $$('.desktop-menu-link:not(.has-submenu)', desktopDrawer || document).forEach(
      (link) => {
        bindOnce(link, 'click', closeDesktopMenu, 'close-menu');
      }
    );
    bindOnce(mobileMenuButton, 'click', toggleMobileMenu, 'toggle-menu');
    bindOnce(mobileOverlay, 'click', closeMobileMenu, 'close-menu');
    bindOnce(
      cartIcon,
      'click',
      (event) => {
        event.preventDefault();
        if (cartDrawer?.classList.contains('active')) {
          hideCart();
        } else {
          showCart();
        }
      },
      'cart-toggle'
    );
    bindOnce(cartClose, 'click', () => hideCart(), 'cart-close');
    bindOnce(cartOverlay, 'click', () => hideCart(), 'cart-overlay-close');
    bindOnce(
      cartDrawer,
      'click',
      async (event) => {
        const quantityButton = event.target.closest('.tomauto-mini-cart-qty__button[data-cart-key]');
        if (quantityButton && cartDrawer.contains(quantityButton)) {
          event.preventDefault();
          if (cartDrawer.getAttribute('aria-busy') === 'true') return;
          await changeCartQuantity(
            quantityButton.dataset.cartKey,
            quantityButton.dataset.quantity
          );
          return;
        }
        const promoToggle = event.target.closest('.tomauto-mini-cart-promo__toggle');
        if (promoToggle && cartDrawer.contains(promoToggle)) {
          const promo = promoToggle.closest('.tomauto-mini-cart-promo');
          const panel = promo?.querySelector('.tomauto-mini-cart-promo__panel');
          const willOpen = promoToggle.getAttribute('aria-expanded') !== 'true';
          state.cartPromoOpen = willOpen;
          promoToggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
          promo?.classList.toggle('is-open', willOpen);
          if (panel) panel.hidden = !willOpen;
          if (willOpen) {
            window.requestAnimationFrame(() => {
              promo?.querySelector('.tomauto-mini-cart-promo__input')?.focus({ preventScroll: true });
            });
          }
        }
      },
      'cart-drawer-actions'
    );
    bindOnce(
      cartDrawer,
      'submit',
      async (event) => {
        const form = event.target.closest('.tomauto-mini-cart-promo__form');
        if (!form || !cartDrawer.contains(form)) return;
        event.preventDefault();
        if (cartDrawer.getAttribute('aria-busy') === 'true') return;
        const input = form.querySelector('.tomauto-mini-cart-promo__input');
        state.cartPromoCode = input?.value?.trim() || '';
        await applyCartDiscount(state.cartPromoCode);
      },
      'cart-promo-submit'
    );
    $$('.mobile-menu-link.has-submenu').forEach((button) => {
      bindOnce(
        button,
        'click',
        () => showMobileSubcategories(button),
        'show-submenu'
      );
    });
    $$('.mobile-back-button').forEach((button) => {
      bindOnce(button, 'click', hideMobileSubcategories, 'back');
    });
    $$(
      '.mobile-menu-link:not(.has-submenu), .mobile-subcategory-link, .mobile-account-link',
      mobileDrawer || document
    ).forEach((link) => {
      bindOnce(link, 'click', closeMobileMenu, 'close-menu');
    });
    bindOnce(
      $('.tm-header__search-form'),
      'submit',
      (event) => {
        event.preventDefault();
        runSearch(document.getElementById('search-bar-input'));
      },
      'search-submit'
    );
    bindOnce(
      $('.mobile-search-bar'),
      'submit',
      (event) => {
        event.preventDefault();
        runSearch(document.getElementById('mobile-search-input'));
      },
      'search-submit'
    );
    populateSearchFields();
  }
  function bindGlobals() {
    if (state.globalsBound) return;
    state.globalsBound = true;
    installCartRequestSync();
    installCartFormInterceptor();
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeDesktopMenu();
        closeMobileMenu();
        if (document.getElementById('cart-dropdown')?.classList.contains('active')) {
          hideCart();
        }
        return;
      }
      trapCartFocus(event);
    });
    window.addEventListener('scroll', scheduleMobileHeaderScroll, {
      passive: true
    });
    const desktopMedia = window.matchMedia('(min-width: 1200px)');
    desktopMedia.addEventListener?.('change', () => {
      closeDesktopMenu();
      closeMobileMenu();
      syncMobileHeaderScroll();
    });
    document.addEventListener('cart:updated', (event) => {
      const cart = event.detail?.cart || event.detail?.cartData;
      if (cart) {
        updateCartHeader(cart);
      }
      if (document.getElementById('cart-dropdown')?.classList.contains('active')) {
        refreshCart({ renderDrawer: true });
      }
    });
    try {
      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          const cart =
            event?.cartData || event?.cart || event?.detail?.cart;
          if (cart) {
            updateCartHeader(cart);
          }
          if (document.getElementById('cart-dropdown')?.classList.contains('active')) {
            refreshCart({ renderDrawer: true });
          }
        });
      }
    } catch {}
    document.addEventListener('shopify:section:load', (event) => {
      init(event.target);
    });
  }
  function init(root = document) {
    const header =
      root.querySelector?.('.tm-header-shell') || $('.tm-header-shell');
    if (!header) return;
    prepareCartPortal();
    bindHeaderElements();
    bindGlobals();
    syncMobileHeaderScroll();
  }
  window.TomautoHeader = {
    init,
    updateCartHeader,
    refreshCart,
    showCart,
    hideCart
  };
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => init(document),
      { once: true }
    );
  } else {
    init(document);
  }
})();
